import { useState } from "react";
import { useParams, useNavigate, Navigate } from "react-router-dom";
import api from "@/lib/api";
import { imgUrl } from "@/lib/format";
import { getVertical } from "@/data/verticals";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, LogIn, UploadCloud, X, ShieldAlert } from "lucide-react";
import { toast } from "sonner";
import { INDIA_STATES } from "@/data/indiaLocations";
import { V_ICONS } from "@/lib/verticalIcons";
import { VerifyDialog } from "@/components/VerifyDialog";

const initSpecs = (config) => {
  const s = {};
  config.specFields.forEach((f) => { s[f.key] = ""; });
  return s;
};

export default function PostListing() {
  const { vertical } = useParams();
  const navigate = useNavigate();
  const config = getVertical(vertical);
  const { user, login } = useAuth();
  const [saving, setSaving] = useState(false);
  const [images, setImages] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [form, setForm] = useState(() => ({
    title: "", description: "", price: "", state: "Karnataka", city: "",
    contact_name: user?.name || "", contact_phone: "",
    ...(config ? { specs: initSpecs(config) } : {}),
  }));

  if (!config) return <Navigate to="/" replace />;
  const Icon = V_ICONS[config.icon];

  if (!user) {
    return (
      <div className="max-w-md mx-auto px-6 py-24 text-center">
        <div className="h-14 w-14 rounded-2xl bg-navy/10 flex items-center justify-center mx-auto"><LogIn className="h-7 w-7 text-navy" /></div>
        <h1 className="mt-5 font-display text-2xl font-semibold text-slate-900">Sign in to post</h1>
        <Button data-testid="listing-login-btn" onClick={login} className="mt-6 bg-navy hover:bg-navy-dark">Sign in with Google</Button>
      </div>
    );
  }

  if (!user.verified) {
    return (
      <div className="max-w-md mx-auto px-6 py-24 text-center">
        <div className="h-14 w-14 rounded-2xl bg-gold/20 flex items-center justify-center mx-auto"><ShieldAlert className="h-7 w-7 text-gold-dark" /></div>
        <h1 className="mt-5 font-display text-2xl font-semibold text-slate-900">Verify your identity to post</h1>
        <p className="mt-2 text-slate-500">Only KYC-verified sellers can list on VYAPAAR. It keeps buyers safe.</p>
        <div className="mt-6"><VerifyDialog trigger={<Button data-testid="listing-verify-btn" className="bg-navy hover:bg-navy-dark text-white">Verify Now</Button>} /></div>
      </div>
    );
  }

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setSpec = (k, v) => setForm((f) => ({ ...f, specs: { ...f.specs, [k]: v } }));

  const handleFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length) return;
    setUploading(true);
    for (const file of files) {
      const fd = new FormData();
      fd.append("file", file);
      try {
        const res = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setImages((prev) => [...prev, res.data.url]);
        toast.success(`${file.name} uploaded`);
      } catch (err) { toast.error(err?.response?.data?.detail || "Upload failed"); }
    }
    setUploading(false);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (images.length < 5) { toast.error("Please upload at least 5 high-definition photos."); return; }
    if (!form.city) { toast.error("Please enter a district / city."); return; }
    const missing = config.specFields.find((f) => f.required && !String(form.specs[f.key] ?? "").trim());
    if (missing) { toast.error(`Please provide ${missing.label}.`); return; }
    setSaving(true);
    try {
      const specs = {};
      config.specFields.forEach((f) => {
        const val = form.specs[f.key];
        specs[f.key] = f.type === "number" ? (val === "" ? null : parseInt(val, 10)) : val;
      });
      const payload = {
        title: form.title, description: form.description, price: parseInt(form.price, 10),
        state: form.state, city: form.city, contact_name: form.contact_name,
        contact_phone: form.contact_phone, images, specs,
      };
      const res = await api.post(`/listings/${vertical}`, payload);
      toast.success("Listed successfully!");
      navigate(`/v/${vertical}/${res.data.id}`);
    } catch { toast.error("Could not create listing. Check all fields."); }
    finally { setSaving(false); }
  };

  const renderSpecField = (f) => {
    if (f.type === "select") {
      return (
        <div key={f.key}><Label className="text-xs">{f.label}</Label>
          <Select value={form.specs[f.key] || undefined} onValueChange={(v) => setSpec(f.key, v)}>
            <SelectTrigger data-testid={`spec-${f.key}`}><SelectValue placeholder={`Select ${f.label}`} /></SelectTrigger>
            <SelectContent className="max-h-72">{f.options.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      );
    }
    return (
      <div key={f.key}><Label className="text-xs">{f.label}</Label>
        <Input data-testid={`spec-${f.key}`} type={f.type === "number" ? "number" : "text"} min={f.min}
          required={f.required} placeholder={f.placeholder} value={form.specs[f.key]} onChange={(e) => setSpec(f.key, e.target.value)} />
      </div>
    );
  };

  return (
    <div className="max-w-3xl mx-auto px-6 sm:px-8 py-10">
      <div className="flex items-center gap-3">
        <div className="h-11 w-11 rounded-xl bg-gold flex items-center justify-center"><Icon className="h-6 w-6 text-navy" /></div>
        <div><h1 className="font-display text-3xl font-semibold tracking-tight text-slate-900">{config.postHeading}</h1><p className="text-slate-500 text-sm">{config.postSub}</p></div>
      </div>

      <form onSubmit={submit} className="mt-8 bg-white border border-slate-200 rounded-2xl p-6 space-y-5">
        <div>
          <Label className="text-xs">Photos</Label>
          <p className="text-xs text-slate-400 mb-2">Upload at least 5 clear, high-definition photos (min 1024×768px).</p>
          <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
            {images.map((url) => (
              <div key={url} className="relative aspect-square rounded-lg overflow-hidden border border-slate-200 group">
                <img src={imgUrl(url)} alt="" className="w-full h-full object-cover" />
                <button type="button" data-testid="listing-remove-image" onClick={() => setImages((p) => p.filter((u) => u !== url))} className="absolute top-1 right-1 h-6 w-6 rounded-full bg-black/60 text-white flex items-center justify-center opacity-100 sm:opacity-0 group-hover:opacity-100 transition-opacity"><X className="h-3.5 w-3.5" /></button>
              </div>
            ))}
            <label data-testid="listing-upload" className="aspect-square rounded-lg border-2 border-dashed border-slate-300 flex flex-col items-center justify-center cursor-pointer hover:border-navy hover:bg-navy/5 transition-colors text-slate-500">
              {uploading ? <Loader2 className="h-6 w-6 animate-spin text-navy" /> : <><UploadCloud className="h-6 w-6" /><span className="text-[11px] mt-1 font-medium">Add photos</span></>}
              <input type="file" accept="image/jpeg,image/png,image/webp" multiple onChange={handleFiles} className="hidden" disabled={uploading} />
            </label>
          </div>
          {images.length < 5 && (
            <p className="mt-2 text-xs text-slate-400">{images.length}/5 photos — at least 5 high-definition photos are required.</p>
          )}
        </div>

        <div><Label className="text-xs">Ad Title</Label><Input data-testid="listing-title" required value={form.title} onChange={(e) => set("title", e.target.value)} placeholder="Short, descriptive title" /></div>
        <div><Label className="text-xs">Description</Label><Textarea data-testid="listing-description" rows={3} required value={form.description} onChange={(e) => set("description", e.target.value)} placeholder="Condition, details, features…" /></div>

        <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
          {config.specFields.map(renderSpecField)}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div><Label className="text-xs">Price (₹){config.priceSuffix ? " per unit" : ""}</Label><Input data-testid="listing-price" type="number" required value={form.price} onChange={(e) => set("price", e.target.value)} /></div>
          <div><Label className="text-xs">State / UT</Label>
            <Select value={form.state} onValueChange={(v) => set("state", v)}>
              <SelectTrigger data-testid="listing-state"><SelectValue /></SelectTrigger>
              <SelectContent className="max-h-72">{INDIA_STATES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label className="text-xs">City</Label><Input data-testid="listing-city" required value={form.city} onChange={(e) => set("city", e.target.value)} placeholder="e.g. Pune" /></div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div><Label className="text-xs">Contact Name</Label><Input data-testid="listing-contact-name" required value={form.contact_name} onChange={(e) => set("contact_name", e.target.value)} /></div>
          <div><Label className="text-xs">Contact Phone</Label><Input data-testid="listing-contact-phone" required value={form.contact_phone} onChange={(e) => set("contact_phone", e.target.value)} placeholder="+91 …" /></div>
        </div>

        <Button data-testid="listing-submit-btn" type="submit" disabled={saving} className="w-full bg-navy hover:bg-navy-dark text-white h-11">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : `List ${config.label}`}
        </Button>
      </form>
    </div>
  );
}
